/**
 * Document Vault surface (routes under /document-vault).
 *
 * The Document Vault is now a set of URL-driven routes. This app declares the
 * routes but owns NO Document Vault data: the `VaultProvider` from
 * `@jsq/document-vault-components` fetches the shared data (spaces, the
 * global-attribute catalog, a documents page) and performs mutations internally
 * via its own GraphQL hooks; the per-route components read that shared context
 * and add only their own query. This app only supplies the signed-in `user`
 * (app auth context, not an API call). Everything runs inside the app-wide
 * ApolloProvider, so queries hit the federation gateway.
 *
 * Route map (base = globalEnv.documentVaultRoute):
 *   /document-vault                       → redirect to /documents
 *   /document-vault/documents             (Filed/Awaiting/All via ?tab=)
 *   /document-vault/documents/:docId      (detail)
 *   /document-vault/sharing               (Rules/Collections/Log via ?tab=)
 *   /document-vault/settings              → redirect to /settings/spaces
 *   /document-vault/settings/spaces       (+ /new, /:id editor)
 *   /document-vault/settings/global | local
 * Transient modals (upload, review, builders, attribute create/edit) have no URL.
 *
 * While the package is yalc-linked, local component edits show up here after
 * `yarn yalc:dev` in the component repo.
 */
import { Redirect, Route, Switch } from "react-router-dom";
import {
  DocumentDetailRoute,
  DocumentsRoute,
  SettingsRoute,
  SharingRoute,
  SpaceEditorRoute,
  VaultLayout,
  VaultProvider,
} from "@jsq/document-vault-components";
import { Flex, LoadingIndicator } from "@jsq/ds/core";
import { useAppContext } from "~/contexts/appContext";
import { globalEnv } from "~/global-env";
import { getRedirectPath, isDataVaultAdmin } from "~/utils/userRoles";
import * as styles from "./styles_vanilla.css";

const base = globalEnv.documentVaultRoute;

export const DocumentVaultComponent = () => {
  const { user } = useAppContext();

  // Second layer of the access guard (the route in src/index.tsx is the first).
  // Mirrors the Data Vault convention so the surface stays closed even if it is
  // ever mounted from somewhere that forgets the route-level check.
  if (!isDataVaultAdmin(user.roles)) {
    return <Redirect to={getRedirectPath(user)} />;
  }

  return (
    <div className={styles.pageContainer} data-testid="document-vault-root">
      <VaultProvider
        user={{
          name: user.fullName || `${user.firstName} ${user.lastName}`.trim() || "Current user",
          role: isDataVaultAdmin(user.roles) ? "Data Vault Admin" : "Data Vault User",
          isSteward: isDataVaultAdmin(user.roles),
        }}
        loadingFallback={
          <Flex alignItems="center" justifyContent="center" className={styles.loadingContainer}>
            <LoadingIndicator />
          </Flex>
        }
      >
        <VaultLayout basePath={base}>
          <Switch>
            <Route path={`${base}/documents/:docId`}>
              <DocumentDetailRoute basePath={base} />
            </Route>
            <Route path={`${base}/documents`}>
              <DocumentsRoute basePath={base} />
            </Route>
            <Route path={`${base}/sharing`}>
              <SharingRoute basePath={base} />
            </Route>
            <Route path={`${base}/settings/spaces/new`}>
              <SpaceEditorRoute basePath={base} />
            </Route>
            <Route path={`${base}/settings/spaces/:id`}>
              <SpaceEditorRoute basePath={base} />
            </Route>
            <Route path={`${base}/settings`}>
              <SettingsRoute basePath={base} />
            </Route>
            <Redirect exact from={base} to={`${base}/documents`} />
          </Switch>
        </VaultLayout>
      </VaultProvider>
    </div>
  );
};
